import React, { useRef } from "react";
import { CalendarDays, Music, PartyPopper } from "lucide-react";
import { useInView } from "../hooks/useInView";

const steps = [
  {
    icon: CalendarDays,
    number: "01",
    heading: "Connect & Plan",
    description:
      "We start with a conversation about your vision, music style, and the flow of your celebration so every moment reflects what matters most to you.",
  },
  {
    icon: Music,
    number: "02",
    heading: "Curate the Experience",
    description:
      "Together we design the soundtrack and timeline for the night, creating the perfect balance of elegance, energy, and unforgettable moments.",
  },
  {
    icon: PartyPopper,
    number: "03",
    heading: "Celebrate the Night",
    description:
      "On your wedding day we handle the music, transitions, and atmosphere so the evening unfolds naturally and your guests stay on the dance floor.",
  },
];

export const ProcessSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { threshold: 0.1 });

  return (
    <section
      ref={sectionRef}
      className="section-padding relative"
      style={{ backgroundColor: "#0F0F0F" }}
    >
      <div className="absolute top-0 left-1/2 w-72 h-72 bg-gold/5 rounded-full blur-3xl -translate-x-1/2" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div
          className={`text-center max-w-2xl mx-auto mb-16 ${
            isInView ? "animate-fade-in-up" : "opacity-0"
          }`}
        >
          <span className="text-gold text-sm font-semibold tracking-widest uppercase mb-4 block">
            Our Process
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            How Your Wedding{" "}
            <span className="text-gold">Comes Together</span>
          </h2>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                className={`relative text-center p-8 lg:p-10 rounded-2xl border border-white/10 ${
                  isInView ? "animate-fade-in-up" : "opacity-0"
                }`}
                style={{
                  animationDelay: `${(index + 1) * 200}ms`,
                  animationFillMode: "both",
                  backgroundColor: "rgba(255,255,255,0.03)",
                }}
              >
                {/* Step Number */}
                <span className="text-gold/20 font-display text-6xl font-bold absolute top-4 right-6">
                  {step.number}
                </span>

                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center mx-auto mb-6">
                  <Icon className="h-7 w-7 text-gold" />
                </div>

                {/* Content */}
                <h3 className="font-display text-xl font-semibold text-white mb-3">
                  {step.heading}
                </h3>
                <p className="text-white/50 leading-relaxed text-sm">
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
